<!-- 
  Component: TeamDaily (HR Management)
  Description: Displays a mobile-responsive list of all employees and their daily attendance status.
  Features:
  - Ionic Card layout tailored for small screens.
  - Color-coded badges for Present, Absent, Late, Off Day, Holiday.
  - Automatically fetches data from backend `hrTeamDaily` API endpoint based on the selected date.
-->
<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar class="custom-toolbar">
        <ion-buttons slot="start">
          <ion-back-button default-href="/home" color="light"></ion-back-button>
        </ion-buttons>
        <ion-title class="custom-title">Team Daily</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true" class="team-daily-content">
      <!-- Date Picker -->
      <div class="date-picker-container">
        <ion-item class="date-item" lines="none">
          <ion-icon :icon="calendarOutline" slot="start" color="primary"></ion-icon>
          <ion-label position="stacked">Select Date</ion-label>
          <ion-datetime 
            display-format="YYYY-MM-DD" 
            v-model="selectedDate" 
            @ionChange="fetchData" 
            mode="ios" 
            presentation="date"
          ></ion-datetime>
        </ion-item>
        
        <!-- Status Filter: Added to filter team members by their daily status -->
        <ion-item class="date-item" lines="none">
          <ion-icon :icon="filterOutline" slot="start" color="secondary"></ion-icon>
          <ion-label position="stacked">Filter by Status</ion-label>
          <ion-select v-model="selectedStatus" @ionChange="fetchData" interface="popover">
            <ion-select-option value="All">All</ion-select-option>
            <ion-select-option value="Present">Present</ion-select-option>
            <ion-select-option value="Absent">Absent</ion-select-option>
            <ion-select-option value="On Leave">On Leave</ion-select-option>
            <ion-select-option value="Late">Late</ion-select-option>
            <ion-select-option value="Off Day">Off Day</ion-select-option>
            <ion-select-option value="Holiday">Holiday</ion-select-option>
          </ion-select>
        </ion-item>
        <!-- Location Filter: Added to filter team members by their check-in/out location -->
        <ion-item class="date-item" lines="none">
          <ion-icon :icon="locationOutline" slot="start" color="tertiary"></ion-icon>
          <ion-label position="stacked">Filter by Location</ion-label>
          <ion-select v-model="selectedLocation" @ionChange="fetchData" interface="popover">
            <ion-select-option value="All">All</ion-select-option>
            <ion-select-option value="Field Visit">Field Visit</ion-select-option>
            <ion-select-option v-for="loc in availableLocations" :key="loc" :value="loc">{{ loc }}</ion-select-option>
          </ion-select>
        </ion-item>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container ion-text-center">
        <ion-spinner name="crescent" color="primary"></ion-spinner>
        <p>Loading team attendance...</p>
      </div>

      <!-- Data List -->
      <div v-else-if="reportData && reportData.length > 0" class="cards-container">
        <ion-card v-for="item in reportData" :key="item.user_id" class="attendance-card">
          <ion-card-content>
            <div class="employee-header">
              <ion-avatar class="employee-avatar">
                <img :src="item.picture || 'https://ui-avatars.com/api/?background=3880ff&color=ffffff&bold=true&name=' + encodeURIComponent(item.name)" />
              </ion-avatar>
              <div class="employee-info">
                <h3 class="employee-name">{{ item.name }}</h3>
                <p class="employee-designation">{{ item.designation || 'Employee' }}</p>
                <p class="shift-name">Shift: {{ item.shift_name }}</p>
              </div>
              <div class="status-badge-container">
                <ion-badge :color="item.status_color" class="status-badge">
                  {{ item.status_text }}
                </ion-badge>
                <ion-badge v-if="item.is_late" color="warning" class="late-badge">Late</ion-badge>
              </div>
            </div>

            <div class="time-logs">
              <!-- In Time and Office display -->
              <div class="time-block" v-if="item.in_time !== '--'">
                <ion-icon :icon="logInOutline" color="success"></ion-icon>
                <div class="time-details">
                  <span>In: {{ item.in_time }}</span>
                  <span class="office-name" v-if="item.in_office">{{ item.in_office }}</span>
                </div>
              </div>
              <div class="time-block" v-else>
                <ion-icon :icon="logInOutline" color="medium"></ion-icon>
                <div class="time-details">
                  <span>In: --</span>
                </div>
              </div>

              <!-- Out Time and Office display -->
              <div class="time-block" v-if="item.out_time !== '--'">
                <ion-icon :icon="logOutOutline" color="danger"></ion-icon>
                <div class="time-details">
                  <span>Out: {{ item.out_time }}</span>
                  <span class="office-name" v-if="item.out_office">{{ item.out_office }}</span>
                </div>
              </div>
              <div class="time-block" v-else>
                <ion-icon :icon="logOutOutline" color="medium"></ion-icon>
                <div class="time-details">
                  <span>Out: --</span>
                </div>
              </div>

              <div class="time-block extra-work" v-if="item.extra_work !== '00:00'">
                <ion-icon :icon="timeOutline" color="tertiary"></ion-icon>
                <span>Extra: {{ item.extra_work }}</span>
              </div>
            </div>
          </ion-card-content>
        </ion-card>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state ion-text-center">
        <ion-icon :icon="calendarOutline" class="empty-icon"></ion-icon>
        <p>No attendance data found for this date.</p>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
/* eslint-disable */
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonItem,
  IonLabel,
  IonInput,
  IonCard,
  IonCardContent,
  IonAvatar,
  IonBadge,
  IonSpinner,
  IonIcon,
  IonSelect,
  IonSelectOption,
  IonDatetime,
  toastController
} from '@ionic/vue';
import { logInOutline, logOutOutline, timeOutline, calendarOutline, peopleOutline, filterOutline, locationOutline } from 'ionicons/icons';

export default {
  name: 'TeamDaily',
  components: {
    IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
    IonButtons, IonBackButton, IonItem, IonLabel, IonInput,
    IonCard, IonCardContent, IonAvatar, IonBadge, IonSpinner, IonIcon,
    IonSelect, IonSelectOption, IonDatetime
  },
  data() {
    return {
      selectedDate: new Date().toISOString().split('T')[0],
      selectedStatus: 'All', // Default filter status
      selectedLocation: 'All', // Default location status
      availableLocations: [],
      reportData: [],
      loading: true,
      logInOutline,
      logOutOutline,
      timeOutline,
      calendarOutline,
      peopleOutline,
      filterOutline,
      locationOutline
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        // Retrieve stored credentials to authenticate the API request
        const username = window.localStorage.getItem('username');
        const password = window.localStorage.getItem('password');
        const token = window.localStorage.getItem('fcm_token');
        
        let cleanDate = this.selectedDate;
        if (cleanDate && cleanDate.includes('T')) {
          cleanDate = cleanDate.split('T')[0];
        }
        
        const payload = {
          username: username,
          password: password,
          token: token,
          date: cleanDate,
          status: this.selectedStatus, // Include status filter in the request
          location: this.selectedLocation // Include location filter
        };

        const response = await this.$http.post('hr/hrTeamDaily', payload);
        if (response.data && response.data.status) {
          this.reportData = response.data.data || [];
          this.availableLocations = response.data.available_locations || [];
        } else {
          this.reportData = [];
          this.showToast(response.data.message || 'Failed to load data.');
        }
      } catch (error) {
        console.error('Error fetching team daily:', error);
        this.reportData = [];
        this.showToast('Network error while fetching data.');
      } finally {
        this.loading = false;
      }
    },
    async showToast(msg) {
      const toast = await toastController.create({
        message: msg,
        duration: 3000,
        position: 'bottom',
        color: 'danger'
      });
      await toast.present();
    }
  }
}
</script>

<style scoped>
.custom-toolbar {
  --background: var(--ion-color-primary);
  --color: white;
}
.custom-title {
  font-weight: 600;
  letter-spacing: 0.5px;
}
.team-daily-content {
  --background: #f4f5f8;
}
.date-picker-container {
  background: white;
  padding: 10px;
  margin-bottom: 10px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}
.date-item {
  --background: transparent;
  --padding-start: 0;
}
.loading-container {
  margin-top: 50px;
  color: var(--ion-color-medium);
}
.cards-container {
  padding: 10px;
}
.attendance-card {
  margin-bottom: 15px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}
.employee-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
}
.employee-avatar {
  width: 50px;
  height: 50px;
  margin-right: 15px;
  border: 2px solid var(--ion-color-light);
}
.employee-info {
  flex-grow: 1;
}
.employee-name {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ion-color-dark);
}
.employee-designation {
  margin: 0 0 2px 0;
  font-size: 12px;
  color: var(--ion-color-medium);
}
.shift-name {
  margin: 0;
  font-size: 11px;
  color: var(--ion-color-medium);
}
.status-badge-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
}
.status-badge {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
}
.late-badge {
  font-size: 10px;
  padding: 3px 6px;
}
.time-logs {
  display: flex;
  justify-content: space-between;
  background: #f8f9fa;
  padding: 10px;
  border-radius: 8px;
}
.time-block {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  font-size: 12px;
  color: var(--ion-color-dark);
  font-weight: 500;
}
.time-details {
  display: flex;
  flex-direction: column;
}
.office-name {
  font-size: 10px;
  color: var(--ion-color-medium);
  margin-top: 2px;
}
.time-block ion-icon {
  font-size: 16px;
}
.extra-work {
  color: var(--ion-color-tertiary);
}
.empty-state {
  margin-top: 80px;
  color: var(--ion-color-medium);
}
.empty-icon {
  font-size: 64px;
  margin-bottom: 15px;
  opacity: 0.5;
}
</style>
